import React, { ForwardedRef, forwardRef, memo } from "react";
import { styled } from '@mui/material/styles';
import Stack from "@mui/material/Stack";
import { active, EDTypography, primary, spacing } from "@trailblazer-game/ed-webkit";
import { ILeagueNFTPrize, ILeagueWeek } from "../../../../helpers/interfaces/league";
import useMediaQuery from "@mui/material/useMediaQuery";
import { formatCurrency } from "../../../../helpers/numbers";
import { IWithLazyLoadProps, withLazyLoad } from "../../../../hooks/withLazyLoad";
import Box from "@mui/material/Box";
import { theme } from "../../../Theme/Theme";

interface ILeagueRewardsProps extends IWithLazyLoadProps {
    className?: string;
    prizes: ILeagueNFTPrize[] | undefined
}

const LeagueRewards = memo(forwardRef(({
                                           prizes,
                                           className
                                       }: ILeagueRewardsProps, ref: ForwardedRef<HTMLDivElement>) => {
    const isSmall = useMediaQuery("@media (max-width:1025.95px)");

    if (!prizes) {
        return null;
    }

    return (
        <Stack className={className} ref={ref} alignItems={"center"}>
            <Stack direction={"column"} spacing={4} className={"content"}>
                <EDTypography variant={"h3"} align={"center"} sx={{ color: primary("main") }}>Rewards</EDTypography>

                <Stack direction={isSmall ? "column" : "row"} spacing={2}>
                    {prizes.map((nftPrize: any, index: any) => (
                        <Stack alignItems={"center"} justifyContent={"center"} spacing={2} key={`prize-${index}`}>
                            {nftPrize.image &&
                                <img src={nftPrize.image} alt={"NFT Image"} className={"nft-image"}/>}
                            <Stack className={"prize-description"} alignItems={"center"} justifyContent={"center"}>
                                <EDTypography sx={{ color: active("primary") }} variant={"h5"}
                                              align={"center"}>
                                    {nftPrize.name}
                                </EDTypography>
                                <EDTypography variant={"body1"} align={"center"}>
                                    x{formatCurrency(nftPrize.amount)}
                                </EDTypography>
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            </Stack>
            {
                isSmall && <Stack className={"mobile-background"}>
                    <Box className={"part top"}/>
                    <Box className={"part middle"}/>
                    <Box className={"part bottom"}/>
                </Stack>
            }
        </Stack>
    );
}));

LeagueRewards.displayName = "LeaguePrizes";

const LeaguePrizesStyled = styled(LeagueRewards)(() => ({
    position: "relative",
    zIndex: 10,
    backgroundImage: "url(/league/rewards-bg.webp)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    aspectRatio: "1.4",
    minWidth: "1025px",
    paddingTop: spacing(12),
    ".nft-image": {
        position: "relative",
        width: "230px",
        height: "230px"
    },
    ".prize-description": {
        width: "230px",
        background: "linear-gradient(180deg, #181818 0%, #171717 71.87%, #1E1E1E 100%)",
        borderWidth: "1px 0px 0px 1px",
        borderStyle: "solid",
        borderColor: "#272626",
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.25)",
        borderRadius: spacing(1),
        padding: spacing(2)
    },
    ".mobile-background": {
        height: "100%",
        width: "80%",
        position: "absolute",
        top: 0,
        ".part": {
            backgroundSize: "contain",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            width: "100%"
        },
        ".top": {
            backgroundImage: "url(/league/rewards-bg-mobile-top.webp)",
            aspectRatio: "4.13"
        },
        ".middle": {
            backgroundImage: "url(/league/rewards-bg-mobile-middle.webp)",
            backgroundRepeat: "repeat-y",
            flexGrow: 1,
            marginTop: "-4px"
        },
        ".bottom": {
            backgroundImage: "url(/league/rewards-bg-mobile-bottom.webp)",
            aspectRatio: "2.98",
            marginTop: "-4px"
        }
    },
    "> .content": {
        position: "relative",
        zIndex: 10
    },
    "@media (max-width:1025.95px)": {
        backgroundImage: "none",
        minWidth: "unset",
        aspectRatio: "unset",
        padding: 0,
        "> .content": {
            padding: theme.spacing(10, 16, 14, 16),
        },
    }
}));

// Export
export default withLazyLoad(LeaguePrizesStyled);
